import { useMemo } from 'react'
import { PaymentRequestQueryResponse } from '../../../../functions/invoicing/fetch-invoicing-payment-requests'

const PaymentRequestStatus = {
  SENT: 'sent',
  PAID: 'paid',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
} as const

/**
 * Custom hook to calculate payment request metrics
 * Handles filtering visible/pending requests and calculating amounts
 */
export const usePaymentRequestCalculations = (
  paymentRequests: PaymentRequestQueryResponse[],
  outstandingBalance?: number
) => {
  // Filter to only show sent/pending and paid requests
  const visibleRequests = useMemo(
    () =>
      paymentRequests.filter(
        request =>
          request.status === PaymentRequestStatus.SENT ||
          request.status === PaymentRequestStatus.PAID
      ),
    [paymentRequests]
  )

  // Filter to only pending (sent) requests
  const pendingRequests = useMemo(
    () =>
      visibleRequests.filter(
        request => request.status === PaymentRequestStatus.SENT
      ),
    [visibleRequests]
  )

  // Calculate amounts in cents, then convert to dollars
  const calculations = useMemo(() => {
    const totalPendingAmountCents = pendingRequests.reduce(
      (sum, request) => sum + request.requestedAmount,
      0
    )

    const overpaymentThresholdCents =
      outstandingBalance !== undefined
        ? outstandingBalance * 100 - totalPendingAmountCents
        : 0

    return {
      totalPendingAmountCents,
      overpaymentThresholdCents,
      totalPendingAmountDollars: totalPendingAmountCents / 100,
      overpaymentThresholdDollars: overpaymentThresholdCents / 100,
    }
  }, [pendingRequests, outstandingBalance])

  return {
    visibleRequests,
    pendingRequests,
    ...calculations,
  }
}
